import Head from 'next/head'

import type { FC, ReactNode } from 'react'
import type { SeoProps } from '@/types'

import { siteConfig } from '@/constants/site'

const absoluteUrl = (value: string): string => /^https?:\/\//.test(value) ? value : `${siteConfig.url}${value.startsWith('/') ? '' : '/'}${value}`

const serializeJsonLd = (value: unknown): string => JSON.stringify(value).replace(/</g, '\\u003c')

export const Seo: FC<SeoProps> = ({
    title,
    description = siteConfig.description,
    ogImage,
    path = '/',
    type = 'website',
    publishedTime,
    modifiedTime,
    tags,
    noIndex = false,
    jsonLd
}): ReactNode => {
    const pageTitle = title ? `${title} - ${siteConfig.name}` : siteConfig.name
    const canonical = absoluteUrl(path)
    const image = absoluteUrl(ogImage ?? siteConfig.ogImage)
    const usesDefaultImage = !ogImage
    const structuredData = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []

    return (
        <Head>
            <title>
                {pageTitle}
            </title>

            <meta name='description' content={description} />
            <link rel='canonical' href={canonical} />
            {noIndex && <meta name='robots' content='noindex, follow' />}

            <meta property='og:site_name' content={siteConfig.name} />
            <meta property='og:locale' content={siteConfig.locale} />
            <meta property='og:title' content={pageTitle} />
            <meta property='og:description' content={description} />
            <meta property='og:type' content={type} />
            <meta property='og:url' content={canonical} />
            <meta property='og:image' content={image} />
            <meta property='og:image:alt' content={`${siteConfig.name} logo`} />
            {usesDefaultImage && <meta property='og:image:width' content={String(siteConfig.ogImageWidth)} />}
            {usesDefaultImage && <meta property='og:image:height' content={String(siteConfig.ogImageHeight)} />}

            {type === 'article' && publishedTime && <meta property='article:published_time' content={publishedTime} />}
            {type === 'article' && modifiedTime && <meta property='article:modified_time' content={modifiedTime} />}
            {type === 'article' && tags?.map((tag) => <meta key={tag} property='article:tag' content={tag} />)}

            <meta name='twitter:card' content='summary_large_image' />
            <meta name='twitter:title' content={pageTitle} />
            <meta name='twitter:description' content={description} />
            <meta name='twitter:image' content={image} />

            {structuredData.map((data, index) => (
                <script
                    key={`ld-${index}`}
                    type='application/ld+json'
                    dangerouslySetInnerHTML={{ __html: serializeJsonLd(data) }}
                />
            ))}
        </Head>
    )
}
